/**
 * Which sports a player plays, as the server reads them from their games, squads and clubs
 * (auth/me's `plays`, PlayerPlays) — never from the sign-up sport alone. The racquet onboarding
 * (the leaderboard welcome and the level questionnaire) is for racquet players only, and a
 * player lands on the profile of the sport they played last.
 *
 * ALL OF IT FOLLOWS THE FOOTBALL V2 SWITCH (the sports registry's multi_sport and football_v2):
 * off, every decision here is what it was before the v2 player work — nobody is a football player
 * to the screens, the welcome and questionnaire are for everyone, the landing is the plain
 * profile — because the endpoints the football screens need are off with it. The server's
 * `plays` says the same when the switch is off; the registry is checked here too so a screen
 * never trusts a `plays` from before the switch changed.
 */
export type Plays = {football: boolean; racquet: boolean; latest: 'football' | 'racquet'};
type WithPlays = {uid?: string; plays?: Plays | null};
/** The two switches, as the sports registry carries them. */
export type FootballFlags = {multi_sport?: boolean; football_v2?: boolean} | null | undefined;

export function footballV2On(registry: FootballFlags): boolean {
    return !!registry?.multi_sport && !!registry?.football_v2;
}

/** Football and nothing racquet: the football sidebar, dashboard and profile. Never with v2 off. */
export function footballPlayer(user: WithPlays | null | undefined, registry: FootballFlags): boolean {
    return footballV2On(registry) && user?.plays?.football === true && user.plays.racquet === false;
}

/** Football beside racquet: the racquet screens with Social Games added. Never with v2 off. */
export function footballToo(user: WithPlays | null | undefined, registry: FootballFlags): boolean {
    return footballV2On(registry) && user?.plays?.football === true && user.plays.racquet === true;
}

/** The "Welcome to the Leaderboard" modal: with v2 on, racquet players once the server has said so; off, as before, everyone. */
export function welcomeRankDue(user: WithPlays | null | undefined, registry: FootballFlags): boolean {
    return !footballV2On(registry) || user?.plays?.racquet === true;
}

/** The racquet questionnaire for an Incomplete player: everyone but, with v2 on, a football-only player. */
export function racquetOnboarding(user: WithPlays | null | undefined, registry: FootballFlags): boolean {
    return !footballV2On(registry) || user?.plays?.racquet !== false;
}

/** Where /profiles/me/ goes for a player: their profile, on Football when v2 is on and that is what they played last. */
export function profileLanding(user: WithPlays & {uid: string}, registry: FootballFlags): {to: string; search?: {sport: 'football'}} {
    return footballV2On(registry) && user.plays?.latest === 'football' && user.plays.football
        ? {to: `/profiles/${user.uid}/`, search: {sport: 'football'}}
        : {to: `/profiles/${user.uid}/`};
}
